import { useContext, useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import API from "Signups/API";
import {
  MoreButton,
  MoreButtonText,
} from "Signups/SignupView/SignupSheet/Screens/Options/Options/MoreButton";
import { signupItemStateHooks } from "Signups/SignupView/SignupSheet/Screens/SignupItems/stateHooks.js";
import { SignupsAppState } from "Signups/state.js";
import { motion } from "framer-motion";

import FnrIcon from "UI/FnrIcon";
import FnrLoadingBricks from "UI/FnrLoadingBricks2";
import { FnrPopover } from "UI/FnrPopover2";
import { HoverChild, HoverParent } from "UI/HoverChildren";
import { scrollbarDarkThin } from "UI/scrollbarStyles";

export default function LinkToGroup({ signupItemID }) {
  const [
    {
      currentSignupList: { ID: signupListID },
    },
    dispatch,
  ] = useContext(SignupsAppState);

  const { getSignupItem } = signupItemStateHooks.useGetSignupItem();

  const [show, setShow] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const buttonRef = useRef();

  const [isLinkingGroup, setIsLinkingGroup] = useState(false);

  const checkPopoverToClose = () => {
    if (isLinkingGroup) return;
    setShow(false);
  };

  useEffect(() => {
    if (show) {
      setIsLoading(true);
      API.getAvailableGroupsForSignupLinking({ signupID: signupListID })
        .post()
        .then((responseData) => {
          setGroups(responseData.availableGroupsForSignupLinking);
          setIsLoading(false);
        });
    }
  }, [show, signupListID]);

  const handleGroupClick = (groupID) => {
    setIsLinkingGroup(groupID);

    API.linkSignupItemToGroup({
      signupID: signupListID,
      signupItemID: signupItemID,
      groupID,
    })
      .post()
      .then((responseData) => {
        getSignupItem(signupItemID);
        dispatch({
          type: "Update Item Group Link",
          linkedToGroup: responseData.linkedToGroup,
          signupItemID: signupItemID,
        });
        setShow(false);
      })
      .catch((e) => {
        alert(
          "Uh oh! There was a problem linking to the group. Please refresh and try again, or contact help@flocknote.com."
        );
      })
      .finally(() => setIsLinkingGroup(false));
  };

  const maxHeight = Math.max(
    window.innerHeight -
      buttonRef.current?.getBoundingClientRect()?.bottom -
      20,
    250
  );

  return (
    <>
      <MoreButton
        ref={buttonRef}
        onClick={() => setShow(true)}
        forceHover={show}
      >
        <FnrIcon fas fa-link />
        <MoreButtonText>Link to a group</MoreButtonText>
      </MoreButton>

      <FnrPopover
        show={show}
        buttonRef={buttonRef}
        onShouldClose={checkPopoverToClose}
      >
        <PopoverContent style={{ maxHeight }}>
          <HelpText>
            When members sign up for this item, they will be automatically added
            to this group. Select a group to link to below.
          </HelpText>
          <ListContainer>
            <LoadingHolder>
              <LoadingInside>
                <FnrLoadingBricks loading={isLoading} size={28} />
              </LoadingInside>
            </LoadingHolder>
            {groups.length > 0 && (
              <HoverParent hoverStyle={{ background: "rgba(255,255,255,.2)" }}>
                <List
                  initial={{ height: 120, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                >
                  {groups.map((group) => (
                    <HoverChild
                      key={group.value}
                      disabled={
                        isLinkingGroup && isLinkingGroup !== group.value
                      }
                      forceHover={isLinkingGroup === group.value}
                    >
                      <GroupLine
                        onClick={
                          isLinkingGroup
                            ? null
                            : () => handleGroupClick(group.value)
                        }
                        className={"indent_" + group.indent}
                        disabled={isLinkingGroup}
                      >
                        <FnrLoadingBricks
                          loading={isLinkingGroup === group.value}
                        >
                          <GroupIcon photoURL={group.photoURL} />
                        </FnrLoadingBricks>
                        <GroupName>{group.name}</GroupName>
                      </GroupLine>
                    </HoverChild>
                  ))}
                </List>
              </HoverParent>
            )}
          </ListContainer>
        </PopoverContent>
      </FnrPopover>
    </>
  );
}

const PopoverContent = styled.div`
  width: 230px;
  overflow: auto;
  ${scrollbarDarkThin}
`;

const LoadingHolder = styled.div`
  position: absolute;
  height: 120px;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const LoadingInside = styled.div`
  width: 28px;
  height: 28px;
  margin-left: -28px;
`;

const ListContainer = styled.div`
  min-height: 120px;
`;

const List = styled(motion.div)`
  margin: 0;
  padding: 0;
`;

const GroupLine = styled.div`
  display: flex;
  align-items: center;
  padding: 6px;
  position: relative;
  z-index: 100;
  cursor: ${({ disabled }) => (disabled ? "wait" : "pointer")};
  &.indent_1 {
    padding-left: 12px;
  }
  &.indent_2 {
    padding-left: 24px;
  }
`;
const GroupIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 24px;
  background: #fff;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
  background-image: url(${(props) => props.photoURL});
  border: 2px solid #fff;
  cursor: pointer;
`;
const GroupName = styled.div`
  margin-left: 6px;
`;

const HelpText = styled.div`
  padding: 8px 0;
  margin: 0 8px;
  font-size: 14px;
  line-height: 17px;
  margin-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
`;
